/**
 * Boli Live - Saved Items Page (Watchlist)
 * 
 * Users can view their saved/favorited auctions here.
 * Uses localStorage to persist watchlist across sessions.
 */

import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Search, Trash2, RefreshCw } from 'lucide-react';
import { getBiddingService } from '@boli/core';
import { AuctionCard, AuctionCardSkeleton } from '../components/AuctionCard';
import { getWatchlistIds } from '../components/WatchlistButton';
import type { Auction } from '@boli/types';

async function fetchAuctionsByIds(ids: string[]): Promise<Auction[]> {
    if (ids.length === 0) return [];

    try {
        const biddingService = getBiddingService();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const config = (biddingService as any).config;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const fetchAdapter = (biddingService as any).fetchAdapter;

        // Fetch each auction by ID (in production, use a batch endpoint)
        const auctions = await Promise.all(
            ids.map(async (id) => {
                try {
                    const response = await fetchAdapter(`${config.apiBaseUrl}/api/auctions/${id}`);
                    if (!response.ok) return null;
                    return response.json();
                } catch {
                    return null;
                }
            })
        );

        return auctions.filter((a): a is Auction => a !== null);
    } catch (error) {
        console.error('Failed to fetch watchlist auctions:', error);
        return [];
    }
}

export default function SavedPage() {
    const [watchlistIds, setWatchlistIds] = useState<string[]>([]);
    const [searchQuery, setSearchQuery] = useState('');

    // Load watchlist IDs on mount and when storage changes
    useEffect(() => {
        const updateWatchlist = () => {
            setWatchlistIds(getWatchlistIds());
        };

        updateWatchlist();
        window.addEventListener('storage', updateWatchlist);

        // Also poll periodically in case of same-tab updates
        const interval = setInterval(updateWatchlist, 1000);

        return () => {
            window.removeEventListener('storage', updateWatchlist);
            clearInterval(interval);
        };
    }, []);

    const { data: auctions, isLoading, refetch } = useQuery<Auction[]>({
        queryKey: ['watchlist', watchlistIds],
        queryFn: () => fetchAuctionsByIds(watchlistIds),
        enabled: watchlistIds.length > 0,
    });

    // Filter by search query
    const filteredAuctions = auctions?.filter(auction =>
        auction.title.toLowerCase().includes(searchQuery.toLowerCase())
    ) || [];

    const clearWatchlist = () => {
        if (window.confirm('Are you sure you want to clear your entire watchlist?')) {
            localStorage.setItem('boli_watchlist', '[]');
            setWatchlistIds([]);
        }
    };

    return (
        <div className="min-h-screen bg-secondary/30 px-4 pt-4 pb-20">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="max-w-7xl mx-auto"
            >
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                            <Heart className="w-6 h-6 text-red-500 fill-current" />
                            Watchlist
                        </h1>
                        <p className="text-sm text-muted-foreground mt-1">
                            {watchlistIds.length} item{watchlistIds.length !== 1 ? 's' : ''} saved
                        </p>
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => refetch()}
                            className="p-2 rounded-full hover:bg-muted transition-colors"
                            title="Refresh"
                        >
                            <RefreshCw className="w-5 h-5 text-muted-foreground" />
                        </button>
                        {watchlistIds.length > 0 && (
                            <button
                                onClick={clearWatchlist}
                                className="p-2 rounded-full hover:bg-red-100 text-red-500 transition-colors"
                                title="Clear watchlist"
                            >
                                <Trash2 className="w-5 h-5" />
                            </button>
                        )}
                    </div>
                </div>

                {/* Search Bar */}
                {watchlistIds.length > 0 && (
                    <div className="relative mb-6">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                        <input
                            type="text"
                            placeholder="Search your watchlist..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-3 bg-card border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                        />
                    </div>
                )}

                {/* Content */}
                <AnimatePresence mode="wait">
                    {watchlistIds.length === 0 ? (
                        /* Empty State */
                        <motion.div
                            key="empty"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="flex flex-col items-center justify-center py-20 text-center bg-card rounded-lg border border-border"
                        >
                            <div className="w-20 h-20 rounded-full bg-secondary flex items-center justify-center mb-6">
                                <Heart className="w-10 h-10 text-muted-foreground" />
                            </div>
                            <h2 className="text-xl font-semibold text-foreground mb-2">No saved items yet</h2>
                            <p className="text-muted-foreground max-w-xs mb-6">
                                Click the heart icon on any auction to add it to your watchlist.
                            </p>
                            <motion.button
                                className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold"
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => window.location.href = '/'}
                            >
                                Browse Auctions
                            </motion.button>
                        </motion.div>
                    ) : isLoading ? (
                        /* Loading State */
                        <motion.div
                            key="loading"
                            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
                        >
                            {Array.from({ length: watchlistIds.length }).map((_: unknown, i: number) => (
                                <AuctionCardSkeleton key={i} />
                            ))}
                        </motion.div>
                    ) : filteredAuctions.length > 0 ? (
                        /* Auction Grid */
                        <motion.div
                            key="auctions"
                            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                        >
                            {filteredAuctions.map((auction, index) => (
                                <motion.div
                                    key={auction.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.3, delay: index * 0.05 }}
                                >
                                    <AuctionCard
                                        auction={{
                                            ...auction,
                                            sellerName: 'Seller',
                                            sellerRating: 4.8,
                                        }}
                                    />
                                </motion.div>
                            ))}
                        </motion.div>
                    ) : (
                        /* No Search Results */
                        <motion.div
                            key="no-results"
                            className="text-center py-12 bg-card rounded-lg border border-border"
                        >
                            <p className="text-muted-foreground">
                                No items match "{searchQuery}"
                            </p>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>
        </div>
    );
}
